import React, { useCallback, useState } from "react";
import { EntityType, RemoveAccessData, ResourceType } from "./entities";
import { SharedAccess, UserAccess } from "target_wasm";
import { AccessItem } from "./AccessItem";
import { PermissionControl } from "./PermissionControl";
import { PendingInviteRow } from "./PendingInviteRow";
import { updateModelUserAccess } from "components/main/utils/models/ModelServices";
import { useAccessUpdater } from "./useAccessUpdater";

interface AccessListProps {
  entityId: string;
  sharedWith: UserAccess[] | undefined;
  resourceType: ResourceType;
  canRevokeAccess: boolean;
  onRevokeAccess: (data: RemoveAccessData) => Promise<void>;
  setSharedWithList?: React.Dispatch<
    React.SetStateAction<SharedAccess | undefined>
  >;
  modelName?: string;
  onResendInvitation?: (email: string) => Promise<void>;
}

export const AccessList: React.FC<AccessListProps> = React.memo((props) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const userCurrentEmail = localStorage.getItem("email");

  const handleRemoveUser = (
    email: string,
    clientId: string,
    oldRole: string,
    isPending?: boolean,
  ) => {
    return async () => {
      await props.onRevokeAccess({ email, clientId, oldRole, isPending });
    };
  };

  const userApiCall = useCallback(
    async (userAccess: UserAccess, newAccess: string) => {
      const userEmail = userAccess.user_js().user_email_js();
      await updateModelUserAccess(props.entityId, userEmail, newAccess);
      props.setSharedWithList?.((prev) =>
        prev?.update_user_role_by_email(userEmail, newAccess),
      );
    },
    [props.entityId, props.setSharedWithList],
  );

  const userUpdater = useAccessUpdater(userApiCall);

  const canChangeAccess =
    props.resourceType === ResourceType.Model &&
    props.entityId !== undefined &&
    props.canRevokeAccess;

  const renderUserAccess = (userAccess: UserAccess) => {
    const permission = userAccess.permission_or_role_js();
    const user = userAccess.user_js();
    const userEmail = user.user_email_js();
    const userId = user.user_id_js();
    const isCurrentUser = userEmail === userCurrentEmail;
    const isPending = userAccess.is_pending_js();

    if (isPending) {
      return (
        <PendingInviteRow
          key={userId}
          email={userEmail}
          canRevokeAccess={props.canRevokeAccess}
          onResend={
            props.onResendInvitation
              ? () => props.onResendInvitation!(userEmail)
              : undefined
          }
          onRevoke={
            props.canRevokeAccess
              ? handleRemoveUser(userEmail, userId, permission, true)
              : undefined
          }
          isActive={openMenuId === userId}
          onMenuToggle={(isOpen) => setOpenMenuId(isOpen ? userId : null)}
        />
      );
    }

    const primaryText = user.user_name_js() || userEmail || "Unknown User";

    return (
      <AccessItem
        key={userId}
        primaryText={primaryText}
        tooltipText={userEmail}
        isActive={openMenuId === userId}
        permissionLabel={
          <PermissionControl
            currentAccess={userAccess.permission_display_js()}
            resourceType={props.resourceType}
            isDisabled={!canChangeAccess && !props.canRevokeAccess}
            isCurrentUser={isCurrentUser}
            isUpdating={userUpdater.updatingIds.has(userId)}
            showSuccess={userUpdater.successIds.has(userId)}
            onAccessChange={
              canChangeAccess
                ? (newAccess) =>
                    userUpdater.trigger(userId, userAccess, newAccess)
                : undefined
            }
            onRemove={
              props.canRevokeAccess && !isCurrentUser
                ? handleRemoveUser(userEmail, userId, permission)
                : undefined
            }
            onMenuToggle={(isOpen) => setOpenMenuId(isOpen ? userId : null)}
            entityName={userEmail}
            entityType={EntityType.User}
          />
        }
        canRevokeAccess={false}
        isCurrentUser={isCurrentUser}
      />
    );
  };

  return (
    <div className="space-y-1 cursor-default overflow-visible">
      {props.sharedWith?.map(renderUserAccess)}
    </div>
  );
});
